// Typed wrappers for the music endpoints. The MusicBrainz typeahead is used
// by both the manual Add-Artist flow on the library page and the Discover
// page's seed picker; the rest of the helpers are read-write CRUD against
// the artist/album resources.

import { api } from "@/lib/api";
import type {
  AddArtistRequest,
  Album,
  AlbumRelease,
  AlbumWithTracks,
  Artist,
  ArtistWithAlbums,
  GrabRecord,
  UpdateAlbumRequest,
  UpdateArtistRequest,
} from "./types";

interface MediaSummary {
  external_ids: {
    musicbrainz?: string | null;
    tmdb?: number | null;
    tvdb?: number | null;
    imdb?: string | null;
  };
  title: string;
  year: number | null;
  overview: string | null;
  poster_url: string | null;
  kind: "movie" | "tv" | "music";
}

export interface ArtistLookupResult {
  mbid: string;
  name: string;
  /** Free-form text from MusicBrainz — "American bluegrass guitarist", etc. */
  disambiguation?: string;
}

export interface MusicStats {
  artists: number;
  monitored_artists: number;
  albums: number;
  monitored_albums: number;
  downloaded_albums: number;
  tracks: number;
  downloaded_tracks: number;
}

export interface ScanStatus {
  running: boolean;
  last_scan_at: string | null;
  unmatched_count: number;
}

export interface UnmatchedFile {
  id: number;
  path: string;
  reason: string;
  scanned_at: string;
}

/** Snapshot from the manual library-integrity scanner. Mirrors
 *  `IntegrityStatus` on the Rust side. */
export interface IntegrityStatus {
  running: boolean;
  last_run_at: string | null;
  albums_incomplete: number;
  tracks_truncated: number;
  /** True while the MusicBrainz duration backfill is sweeping —
   *  the truncation scanner's coverage will keep growing until
   *  the backfill drains. */
  backfill_running: boolean;
  /** How many local tracks still lack a `duration_ms`. Trends to
   *  zero as the backfill catches up. */
  tracks_pending_duration: number;
}

/** One album the live-recording cleanup would drop. Mirrors
 *  `LiveCleanupCandidate` on the Rust side. */
export interface LiveCleanupCandidate {
  album_id: number;
  artist_id: number;
  artist_name: string;
  title: string;
  primary_type: string | null;
  /** JSON-encoded `Vec<String>` straight from the column. Parse on
   *  the frontend if a render needs the secondary types. */
  secondary_types: string | null;
  has_file: boolean;
}

/** Preview response from `GET /api/v1/music/cleanup/live-recordings`. */
export interface LiveCleanupResponse {
  matched: number;
  candidates: LiveCleanupCandidate[];
}

/** One row from `music_integrity_issues`, joined with album + artist
 *  + track names. Mirrors `IntegrityIssue` on the Rust side. */
export interface IntegrityIssue {
  id: number;
  /** `album_incomplete` | `track_truncated` */
  kind: string;
  album_id: number | null;
  album_title: string | null;
  artist_name: string | null;
  artist_id: number | null;
  track_id: number | null;
  track_title: string | null;
  track_number: number | null;
  disc_number: number | null;
  expected_tracks: number | null;
  filed_tracks: number | null;
  /** JSON-encoded array of MB track numbers missing from disk. */
  missing_track_numbers: string | null;
  expected_duration_ms: number | null;
  actual_duration_ms: number | null;
  shortfall_pct: number | null;
  path: string | null;
  detected_at: string;
}

export interface MusicSettingsResponse {
  library_path: string | null;
  library_path_source: "db" | "env" | null;
  scan_interval_hours: number;
  last_scan_at: string | null;
  /** Fetch `.lrc` lyrics sidecars on import + daily backfill (issue #16). */
  fetch_lyrics: boolean;
  /** Issue #17: rewrite audio tags from MusicBrainz canonical metadata
   *  on import. Default `false` — opt-in only since this rewrites
   *  user files. */
  write_tags_on_import: boolean;
  /** Periodic MusicBrainz refresh cadence for monitored artists.
   *  `0` disables; default 24 (issue #6). */
  refresh_interval_hours: number;
  /** ISO-8601 timestamp of the last completed refresh tick. */
  last_refresh_at: string | null;
  /** Issue #10: how often the music RSS-sync loop fires a fresh
   *  search for monitored albums whose release_date has passed but
   *  `has_file=0`. Integer minutes; `0` disables; default 30. */
  rss_interval_minutes: number;
  /** Issue #21: per-segment path templates. Defaults reproduce the
   *  legacy hard-coded layout (`{Artist}/{Album}/{Track:02} - {Title}.{ext}`). */
  path_template_artist_dir: string;
  path_template_album_dir: string;
  path_template_filename: string;
  /** Plex Media Server base URL — drives the Spotify-import →
   *  Plex-playlist materialiser. NULL/blank disables Plex
   *  features. */
  plex_base_url: string | null;
  /** Whether a `plex.token` is currently stored. Backend never
   *  returns the actual token; the UI uses this to show a "set" /
   *  "unset" affordance and a `<input type=password>` to write a
   *  new one. */
  plex_token_set: boolean;
}

/** One row in the Spotify-import jobs list. Status:
 *    pending           — POST accepted, resolver hasn't started
 *    resolving         — resolver is walking MB
 *    resolved          — all MB lookups done, grabs queued
 *    playlist_created  — Plex playlist exists
 *    failed            — resolver bailed; see `error` */
export interface SpotifyImportJob {
  id: number;
  playlist_name: string;
  source_spotify_id: string | null;
  status: string;
  error: string | null;
  plex_playlist_rating_key: string | null;
  plex_playlist_created_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SpotifyImportTrack {
  id: number;
  track_index: number;
  track_title: string;
  artist_name: string;
  album_name: string;
  spotify_track_id: string | null;
  spotify_album_id: string | null;
  resolved_artist_id: number | null;
  resolved_album_id: number | null;
  plex_rating_key: string | null;
  resolution_note: string | null;
}

export interface SpotifyImportDetail extends SpotifyImportJob {
  tracks: SpotifyImportTrack[];
}

/** Returned by `POST /api/v1/music/spotify-import/:id/plex-playlist`. */
export interface PlaylistCreationResult {
  plex_playlist_rating_key: string;
  matched_tracks: number;
  missing_tracks: number;
}

/** Issue #10: per-tick summary returned by the manual
 *  `POST /api/v1/music/rss-sync/run` endpoint. */
export interface RssSyncRunResponse {
  considered: number;
  dispatched: number;
  skipped: number;
  failed: number;
}

export interface ArtistRefreshResponse {
  /** Number of release-groups MB returned that we hadn't seen
   *  before — gets surfaced in the toast after a manual refresh. */
  new_albums: number;
}

/** Issue #3: a single row in the per-album interactive-search picker.
 *  Mirrors the backend `InteractiveCandidate` shape — same fields the
 *  selector uses to pick the auto-grab winner are surfaced to the user
 *  so they can override on equal terms. */
export interface InteractiveCandidate {
  indexer_id: number;
  indexer_name: string;
  release_title: string;
  release_guid: string;
  size: number;
  seeders: number | null;
  publish_date: string | null;
  /** Detected quality slug (e.g. "flac-lossless", "mp3-320"). NULL when
   *  the parser couldn't classify the title. */
  quality_slug: string | null;
  /** Custom-format score sum. May be negative for penalty formats. */
  score: number;
  /** `quality_slug` currently filed against the album. NULL when the
   *  album has no file yet — the upgrade indicator won't render. */
  current_quality_slug: string | null;
  /** True iff this release was previously blocked for this album.
   *  Surfaced as a warning chip; the user can still override. */
  blocklisted: boolean;
  download_url: string;
}

export interface InteractiveSearchResponse {
  /** Query string the backend actually executed against indexers —
   *  the override when the caller passed one, else the auto-built
   *  `{artist} {album}`. Surfaced so the modal can prefill its
   *  search-term input with the resolved value. */
  query: string;
  candidates: InteractiveCandidate[];
}

export interface LyricsBackfillSummary {
  considered: number;
  written: number;
  misses: number;
}

/**
 * Row shape returned by `GET /api/v1/music/wanted`. Joined view of
 * (album + artist) — the artist columns are duplicated per row but
 * that's how Movies/TV's wanted endpoints model it too. Frontend
 * keys grouping/filtering off `artist_id` if it ever wants to
 * collapse rows by artist.
 */
export interface WantedAlbum {
  album_id: number;
  artist_id: number;
  artist_name: string;
  artist_poster_url: string | null;
  title: string;
  release_date: string | null;
  primary_type: string | null;
  /** JSON-encoded list of MusicBrainz secondary types — same shape as
   *  `Album.secondary_types`. NULL for canonical releases. */
  secondary_types: string | null;
  /** MusicBrainz disambiguation — see `Album.disambiguation`. */
  disambiguation: string | null;
  poster_url: string | null;
}

export const musicApi = {
  /** Search MusicBrainz for artists by name. Empty `q` is rejected server-side. */
  lookupArtists: async (q: string): Promise<ArtistLookupResult[]> => {
    const raw = await api<MediaSummary[]>(
      `/api/v1/artists/lookup?q=${encodeURIComponent(q)}`,
    );
    return raw
      .filter((r) => r.kind === "music" && !!r.external_ids.musicbrainz)
      .map((r) => ({
        mbid: r.external_ids.musicbrainz!,
        name: r.title,
        disambiguation: r.overview ?? undefined,
      }));
  },

  // Artists -------------------------------------------------------------------
  // TODO: backend list endpoint with album counts to avoid N+1 lookups when
  // we want to render `{N} albums` on the library card grid.
  listArtists: () => api<Artist[]>("/api/v1/artists"),
  getArtist: (id: number) =>
    api<ArtistWithAlbums>(`/api/v1/artists/${id}`),
  addArtist: (body: AddArtistRequest) =>
    api<ArtistWithAlbums>("/api/v1/artists", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateArtist: (id: number, body: UpdateArtistRequest) =>
    api<Artist>(`/api/v1/artists/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteArtist: (id: number) =>
    api<void>(`/api/v1/artists/${id}`, { method: "DELETE" }),
  /**
   * Re-run the fanart.tv artwork lookup for this artist (issue
   * #12). Patches `fanart_url` / `banner_url` / `logo_url` plus
   * any per-album `disc_art_url` whose column is still NULL.
   * Admin only — bound by one upstream HTTP round-trip so the
   * response reflects the persisted state. Returns the freshly-
   * read artist row.
   */
  refreshArtwork: (id: number) =>
    api<Artist>(`/api/v1/artists/${id}/refresh-artwork`, {
      method: "POST",
    }),
  listArtistAlbums: (id: number) =>
    api<Album[]>(`/api/v1/artists/${id}/albums`),
  /** `POST /api/v1/artists/:id/refresh` — manually re-pull the artist
   *  from MusicBrainz. New release-groups land as new `albums`
   *  rows monitored to match the artist (issue #6). */
  refreshArtist: (id: number) =>
    api<ArtistRefreshResponse>(`/api/v1/artists/${id}/refresh`, {
      method: "POST",
    }),

  // Albums --------------------------------------------------------------------
  getAlbum: (id: number) =>
    api<AlbumWithTracks>(`/api/v1/albums/${id}`),
  updateAlbum: (id: number, body: UpdateAlbumRequest) =>
    api<Album>(`/api/v1/albums/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  searchAlbum: (id: number) =>
    api<GrabRecord>(`/api/v1/albums/${id}/search`, { method: "POST" }),
  /**
   * Issue #3: same indexer fan-out as `searchAlbum`, but returns the
   * full ranked candidate list instead of dispatching the top one.
   * The picker calls `grabAlbumRelease` to dispatch a chosen row.
   */
  searchAlbumInteractive: (id: number, query?: string) => {
    // Empty / whitespace-only query falls through to the backend
    // default ("{artist} {album}"). Send no `?query=` at all in that
    // case so the URL stays clean and the backend treats it as the
    // unset case unambiguously.
    const trimmed = query?.trim();
    const qs = trimmed ? `?query=${encodeURIComponent(trimmed)}` : "";
    return api<InteractiveSearchResponse>(
      `/api/v1/albums/${id}/search-interactive${qs}`,
      { method: "POST" },
    );
  },
  /**
   * Issue #3: dispatch one specific candidate the user picked from
   * the interactive-search modal. Persists a `grabs` row tied to the
   * album, same as the auto path.
   */
  grabAlbumRelease: (
    id: number,
    body: {
      indexer_id: number;
      release_title: string;
      download_url: string;
      release_guid?: string;
      size?: number;
    },
  ) =>
    api<GrabRecord>(`/api/v1/albums/${id}/grab-release`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteAlbum: (id: number) =>
    api<void>(`/api/v1/albums/${id}`, { method: "DELETE" }),
  /**
   * Issue #7: candidate releases / editions within this album's
   * release-group. Lazy-loaded from MusicBrainz on first call;
   * cached in `album_releases` thereafter. Failures of the upstream
   * MB fetch return whatever's locally cached (possibly empty); the
   * release-group-only flow keeps working in that case.
   */
  listAlbumReleases: (id: number) =>
    api<AlbumRelease[]>(`/api/v1/albums/${id}/releases`),

  /** Monitored albums (under monitored artists) without a file. */
  wanted: (limit: number = 200) =>
    api<WantedAlbum[]>(`/api/v1/music/wanted?limit=${limit}`),

  // Library-wide counts for the dashboard.
  stats: () => api<MusicStats>("/api/v1/music/stats"),

  // Library integrity scanner (issue: manual crawl for incomplete
  // albums + truncated tracks).
  integrity: {
    /** Kick a manual scan in the background. Returns 202 immediately
     *  on success; 409 if a scan is already in flight. */
    start: () =>
      api<{ ok: boolean }>("/api/v1/music/integrity/scan", { method: "POST" }),
    /** Poll status + summary counts from the current contents of
     *  `music_integrity_issues`. */
    status: () => api<IntegrityStatus>("/api/v1/music/integrity/status"),
    /** Kick a MusicBrainz duration backfill in the background.
     *  Returns 202 + `{ok}` on successful start, 409 if a sweep is
     *  already in flight. Progress is observable via the
     *  `backfill_running` + `tracks_pending_duration` fields on
     *  `status()`. */
    backfill: () =>
      api<{ ok: boolean }>("/api/v1/music/integrity/backfill", {
        method: "POST",
      }),
    /** Fetch issue rows, optionally filtered by kind. */
    issues: (params: { kind?: "album_incomplete" | "track_truncated"; limit?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.kind) qs.set("kind", params.kind);
      if (params.limit != null) qs.set("limit", String(params.limit));
      const suffix = qs.toString();
      return api<IntegrityIssue[]>(
        `/api/v1/music/integrity/issues${suffix ? `?${suffix}` : ""}`,
      );
    },
  },

  // Live-recording cleanup. GET = preview, DELETE = commit. The
  // backend runs the same title + metadata filter the import path
  // would now reject; this lets the user delete albums imported
  // before the filter learned about title patterns.
  liveCleanup: {
    preview: () =>
      api<LiveCleanupResponse>("/api/v1/music/cleanup/live-recordings"),
    delete: () =>
      api<{ deleted: number }>(
        "/api/v1/music/cleanup/live-recordings",
        { method: "DELETE" },
      ),
  },

  // Library scanner.
  scan: {
    start: () =>
      api<{ ok: boolean }>("/api/v1/music/scan", { method: "POST" }),
    status: () => api<ScanStatus>("/api/v1/music/scan/status"),
    unmatched: (limit: number = 100) =>
      api<UnmatchedFile[]>(`/api/v1/music/scan/unmatched?limit=${limit}`),
    claim: (
      unmatchedId: number,
      body: { album_id: number; track_number?: number },
    ) =>
      api<void>(
        `/api/v1/music/scan/unmatched/${unmatchedId}/claim`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      ),
  },

  // Music settings (library path + scan interval + lyrics opt-in).
  settings: {
    get: () => api<MusicSettingsResponse>("/api/v1/settings/music"),
    update: (body: {
      library_path?: string;
      scan_interval_hours?: number;
      fetch_lyrics?: boolean;
      write_tags_on_import?: boolean;
      refresh_interval_hours?: number;
      rss_interval_minutes?: number;
      path_template_artist_dir?: string;
      path_template_album_dir?: string;
      path_template_filename?: string;
      /** Plex Media Server base URL. Empty string clears. */
      plex_base_url?: string;
      /** Plex X-Plex-Token. Empty string clears. */
      plex_token?: string;
    }) =>
      api<MusicSettingsResponse>("/api/v1/settings/music", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },

  // Spotify-playlist import. POST = create + kick resolver,
  // GET    = list recent jobs,
  // GET id = job + per-track detail,
  // POST id/plex-playlist = materialise in Plex (looks each track
  //   up by artist/album/title; creates the audio playlist with
  //   whatever matches).
  spotifyImport: {
    create: (payload: {
      playlist_name: string;
      source_spotify_id?: string;
      tracks: {
        track: string;
        artist: string;
        album: string;
        spotify_track_id?: string;
        spotify_album_id?: string;
      }[];
    }) =>
      api<{ id: number }>("/api/v1/music/spotify-import", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    list: () =>
      api<SpotifyImportJob[]>("/api/v1/music/spotify-import"),
    get: (id: number) =>
      api<SpotifyImportDetail>(`/api/v1/music/spotify-import/${id}`),
    createPlexPlaylist: (id: number) =>
      api<PlaylistCreationResult>(
        `/api/v1/music/spotify-import/${id}/plex-playlist`,
        { method: "POST" },
      ),
  },

  // Issue #10: kick the music RSS-sync loop on demand. Returns the
  // per-tick summary so the user can see what landed.
  rssSync: {
    runNow: () =>
      api<RssSyncRunResponse>("/api/v1/music/rss-sync/run", {
        method: "POST",
      }),
  },

  // Lyrics — issue #16. The `.lrc` content is plain text; the
  // backfill endpoint kicks the same sweep the daily loop runs.
  lyrics: {
    get: (trackId: number): Promise<string> =>
      fetch(`/api/v1/tracks/${trackId}/lyrics`, {
        credentials: "include",
      }).then(async (r) => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        return r.text();
      }),
    backfill: () =>
      api<LyricsBackfillSummary>("/api/v1/music/lyrics/backfill", {
        method: "POST",
      }),
  },
};
